import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Plus, Trash2, ToggleRight, ToggleLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";

const SEASONS = [
  { id: "launch", label: "🚀 Launch Sale" },
  { id: "winter", label: "❄ Winter Sale" },
  { id: "spring", label: "🌸 Spring Sale" },
  { id: "summer", label: "☀ Summer Sale" },
  { id: "fall",   label: "🍂 Fall Sale" },
  { id: "custom", label: "✨ Custom" },
];

const DEFAULTS = {
  season: "custom",
  enabled: true,
  prices: { premium: 20, executive: 50 },
  banner_text: "Limited-time seasonal sale — 50% off Premium & Executive plans",
  badge_text_premium: "Most Popular",
  badge_text_executive: "Best Value",
  show_countdown: true,
  overlap: false,
  theme_color: "#00ff9c",
};

export default function AdminPromotions() {
  const [promos, setPromos] = useState([]);
  const [form, setForm] = useState(DEFAULTS);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/api/admin/promotions");
      setPromos(data || []);
    } catch (e) { toast.error("Failed to load promotions"); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        prices: {
          premium: parseFloat(form.prices.premium),
          executive: parseFloat(form.prices.executive),
        },
        starts_at: form.starts_at || null,
        ends_at: form.ends_at || null,
      };
      await api.post("/api/admin/promotions", payload);
      toast.success("Promotion created");
      setForm(DEFAULTS);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Create failed");
    } finally { setBusy(false); }
  };

  const toggle = async (p) => {
    try {
      await api.post(`/api/admin/promotions/${p.id}/toggle`);
      await load();
    } catch (err) { toast.error("Toggle failed"); }
  };
  const del = async (p) => {
    if (!confirm(`Delete promotion "${p.banner_text || p.season}"?`)) return;
    try {
      await api.delete(`/api/admin/promotions/${p.id}`);
      await load();
    } catch (err) { toast.error("Delete failed"); }
  };

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-4" data-testid="admin-promotions">
      <div className="card-surface p-4">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-3.5 h-3.5 text-[var(--bull)]" />
          <div className="text-white text-[14px] font-semibold">New Promotion</div>
        </div>
        <div className="space-y-2.5">
          <div>
            <label className="label-mono">Season</label>
            <select value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })}
                    className="input-glass w-full mt-1" data-testid="promo-season">
              {SEASONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label-mono">Premium $</label>
              <input type="number" value={form.prices.premium}
                     onChange={(e) => setForm({ ...form, prices: { ...form.prices, premium: e.target.value } })}
                     className="input-glass w-full mt-1" data-testid="promo-premium-price"/>
            </div>
            <div>
              <label className="label-mono">Executive $</label>
              <input type="number" value={form.prices.executive}
                     onChange={(e) => setForm({ ...form, prices: { ...form.prices, executive: e.target.value } })}
                     className="input-glass w-full mt-1" data-testid="promo-executive-price"/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label-mono">Starts</label>
              <input type="datetime-local" value={form.starts_at || ""}
                     onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                     className="input-glass w-full mt-1" data-testid="promo-starts"/>
            </div>
            <div>
              <label className="label-mono">Ends</label>
              <input type="datetime-local" value={form.ends_at || ""}
                     onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
                     className="input-glass w-full mt-1" data-testid="promo-ends"/>
            </div>
          </div>
          <div>
            <label className="label-mono">Banner text</label>
            <input value={form.banner_text}
                   onChange={(e) => setForm({ ...form, banner_text: e.target.value })}
                   className="input-glass w-full mt-1" data-testid="promo-banner-text"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label-mono">Premium badge</label>
              <input value={form.badge_text_premium}
                     onChange={(e) => setForm({ ...form, badge_text_premium: e.target.value })}
                     className="input-glass w-full mt-1"/>
            </div>
            <div>
              <label className="label-mono">Executive badge</label>
              <input value={form.badge_text_executive}
                     onChange={(e) => setForm({ ...form, badge_text_executive: e.target.value })}
                     className="input-glass w-full mt-1"/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label-mono">Theme color</label>
              <input type="color" value={form.theme_color}
                     onChange={(e) => setForm({ ...form, theme_color: e.target.value })}
                     className="input-glass w-full mt-1 h-9"/>
            </div>
            <div className="flex flex-col justify-end">
              <label className="flex items-center gap-2 text-[12px] text-white/80 cursor-pointer">
                <input type="checkbox" checked={!!form.show_countdown}
                       onChange={(e) => setForm({ ...form, show_countdown: e.target.checked })}/>
                Show countdown
              </label>
              <label className="flex items-center gap-2 text-[12px] text-white/80 cursor-pointer mt-1">
                <input type="checkbox" checked={!!form.overlap}
                       onChange={(e) => setForm({ ...form, overlap: e.target.checked })}/>
                Allow overlap
              </label>
            </div>
          </div>
          <button onClick={create} disabled={busy}
                  className="btn-accent w-full inline-flex items-center justify-center gap-2 mt-2"
                  data-testid="promo-create-btn">
            <Plus className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Create promotion"}
          </button>
        </div>
      </div>

      <div className="card-surface p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
          <div className="text-white text-[14px] font-semibold">All promotions ({promos.length})</div>
        </div>
        <div className="divide-y divide-white/5">
          {promos.length === 0 && <div className="text-center text-white/40 py-8 text-xs">No promotions</div>}
          {promos.map((p) => (
            <div key={p.id} className="px-4 py-3 flex items-center gap-3" data-testid={`promo-row-${p.id}`}>
              <div className="w-2 h-2 rounded-full"
                   style={{ background: p.enabled ? "var(--bull)" : "rgba(255,255,255,0.2)" }}/>
              <div className="flex-1 min-w-0">
                <div className="text-white text-[13px] font-semibold tracking-tight">
                  {SEASONS.find((s) => s.id === p.season)?.label || p.season}
                </div>
                <div className="text-[11px] text-white/50 truncate">{p.banner_text}</div>
                <div className="mt-1 label-mono">
                  Premium ${p.prices?.premium} · Executive ${p.prices?.executive}
                  {p.starts_at && ` · ${new Date(p.starts_at).toLocaleDateString()}`}
                  {p.ends_at && ` → ${new Date(p.ends_at).toLocaleDateString()}`}
                </div>
              </div>
              <button onClick={() => toggle(p)} className="text-white/70 hover:text-white p-1"
                      data-testid={`promo-toggle-${p.id}`}>
                {p.enabled
                  ? <ToggleRight className="w-5 h-5 text-[var(--bull)]" />
                  : <ToggleLeft className="w-5 h-5 text-white/40" />}
              </button>
              <button onClick={() => del(p)} className="text-white/40 hover:text-[var(--bear)] p-1"
                      data-testid={`promo-delete-${p.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
